import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { adminApi } from '../../services/api';
import SalesTablePage from '../../components/sales/shared/SalesTablePage';
import {
  updateLead,
  setLeadsData,
  trackCellEdit,
  clearEditedRows,
  clearEditedRowsForLead,
  discardEdits,
  setLoading,
  setRefreshing,
  setSaving,
  setError,
  clearError,
  selectLeadsData,
  selectEditedRows,
  selectEditedRowsCount,
  selectLoading,
  selectRefreshing,
  selectSaving,
  selectError,
} from '../../components/redux/slices/sales-module/salesLeadsSlice';

/**
 * Sales Leads Page
 * Thin wrapper around SalesTablePage configured for leads
 */
const SalesLeadsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [converting, setConverting] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  // Handler for converting lead to opportunity
  const handleConvertToOpportunity = useCallback(async (lead) => {
    // Check if already converted (API returns 'converted_opportunity', not 'converted_opportunity_id')
    if (lead.converted_opportunity) {
      setStatusMessage({
        type: 'warning',
        message: `This lead has already been converted to an opportunity (ID: ${lead.converted_opportunity})`
      });
      return;
    }

    setConverting(true);
    setStatusMessage(null);

    try {
      const response = await adminApi.convertLeadToOpportunity(lead.id);

      if (response.data && response.data.success) {
        setStatusMessage({
          type: 'success',
          message: `Lead successfully converted to opportunity for "${lead.company_object?.name || lead.lead_data?.company || 'this lead'}"`,
          opportunityId: response.data.opportunity_id
        });

        // Patch only this lead's converted_opportunity field in Redux — no full page reload needed
        dispatch(updateLead({ id: lead.id, converted_opportunity: response.data.opportunity_id }));
      } else {
        setStatusMessage({
          type: 'error',
          message: `Failed to convert lead: ${response.data?.message || 'Unknown error'}`
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to convert lead';
      setStatusMessage({
        type: 'error',
        message: `Error converting lead: ${errorMsg}`
      });
    } finally {
      setConverting(false);
    }
  }, [dispatch]);

  // Render convert button
  const renderConvertButton = useCallback((lead) => {
    const isArchived = lead.is_active === false || lead.is_active === 'false';
    if (isArchived) return null;

    const alreadyConverted = lead.converted_opportunity;

    return (
      <button
        className={`btn btn-sm ${alreadyConverted ? 'btn-secondary' : 'btn-primary'}`}
        onClick={() => handleConvertToOpportunity(lead)}
        disabled={converting || alreadyConverted}
        title={alreadyConverted ? 'Already converted to opportunity' : 'Convert to Sales Opportunity'}
      >
        {converting ? (
          <>
            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            Converting...
          </>
        ) : (
          <>
            {alreadyConverted ? 'Converted' : 'To Opp'}
            <i className={`fas ${alreadyConverted ? 'fa-check' : 'fa-rocket'} ml-1`}></i>
          </>
        )}
      </button>
    );
  }, [converting, handleConvertToOpportunity]);

  const config = useMemo(() => ({
    entityType: 'lead',
    entityName: 'Sales Lead',
    entityNamePlural: 'Sales Leads',
    basePath: '/admin/sales/leads',
    api: {
      getAll: adminApi.getSalesLeads,
      getOne: adminApi.getSalesLead,
      update: adminApi.updateSalesLead,
      delete: adminApi.deleteSalesLead,
    },
    redux: {
      actions: {
        setData: setLeadsData,
        updateEntity: updateLead,
        trackCellEdit,
        clearEditedRows,
        clearEditedRowsForEntity: clearEditedRowsForLead,
        discardEdits,
        setLoading,
        setRefreshing,
        setSaving,
        setError,
        clearError,
      },
      selectData: selectLeadsData,
      selectEditedRows,
      selectEditedRowsCount,
      selectLoading,
      selectRefreshing,
      selectSaving,
      selectError,
    },
    onStatusMessage: setStatusMessage,
    customActions: {
      renderConvertButton,
    },
  }), [renderConvertButton]);

  return (
    <>
      {statusMessage && (
        <div className={`alert alert-${statusMessage.type === 'error' ? 'danger' : statusMessage.type} alert-dismissible fade show`} role="alert">
          <i className={`fas fa-${statusMessage.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
          {statusMessage.message}
          {statusMessage.opportunityId && (
            <div className="mt-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={() => navigate(`/admin/sales/opportunities?id=${statusMessage.opportunityId}&panel=lead`)}
              >
                View Opportunity
              </button>
            </div>
          )}
          <button
            type="button"
            className="btn-close"
            onClick={() => setStatusMessage(null)}
            aria-label="Close"
          ></button>
        </div>
      )}
      <SalesTablePage config={config} />
    </>
  );
};

export default SalesLeadsPage;
